import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Search,
  Home,
  Tv,
  Film,
  Radio,
  Bookmark,
  Command
} from 'lucide-react';
import CommandPalette from './CommandPalette';
import { useWatchlist } from '../hooks/useWatchlist';

const Navbar = () => {
  const [isCmdOpen, setIsCmdOpen] = useState(false);
  const location = useLocation();
  const { watchlist } = useWatchlist();

  // Listen for Cmd+K or Ctrl+K globally
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsCmdOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Command Palette Modal */}
      <CommandPalette
        isOpen={isCmdOpen}
        onClose={() => setIsCmdOpen(false)}
        watchlistCount={watchlist.length}
      />

      {/* Desktop Sidebar (Left Panel) */}
      <aside className="bg-[#0D0F17]/85 backdrop-blur-2xl hidden lg:flex flex-col w-64 fixed inset-y-0 left-0 z-40 border-r border-white/10 shadow-2xl">
        {/* Brand Header */}
        <div className="p-6 pb-6 flex items-center justify-between">
          <Link to="/" className="group flex items-center gap-2.5">
            <div className="flex flex-col">
              <span className="text-2xl font-extrabold tracking-tight text-white flex items-center gap-1 font-display">
                Binge<span className="text-orange-500">HQ</span>
              </span>
            </div>
          </Link>
        </div>

        {/* Cmd+K Search Trigger Pill */}
        <div className="px-4 mb-6">
          <button
            onClick={() => setIsCmdOpen(true)}
            className="w-full flex items-center justify-between px-3.5 py-2.5 bg-white/5 border border-white/10 rounded-2xl text-slate-400 hover:text-white hover:bg-white/10 hover:border-orange-500/30 transition-all duration-300 group shadow-sm focusable"
          >
            <div className="flex items-center gap-2.5">
              <Search size={17} className="text-orange-500 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium">Search...</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-lg bg-white/10 text-slate-300 border border-white/10">
              <Command size={10} /> K
            </div>
          </button>
        </div>

        {/* Main Navigation Links */}
        <nav className="flex-1 px-4 space-y-1.5 overflow-y-auto hide-scrollbar">
          <Link
            to="/"
            className={`flex items-center gap-3.5 px-4 py-3 font-semibold rounded-2xl text-sm transition-all duration-300 focusable ${isActive('/')
              ? 'text-white bg-gradient-to-r from-orange-500/20 to-orange-500/5 border border-orange-500/30 shadow-md shadow-orange-500/10'
              : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'
              }`}
          >
            <Home size={19} className={isActive('/') ? 'text-orange-400' : 'text-slate-400'} />
            <span>Home</span>
          </Link>

          <Link
            to="/movies"
            className={`flex items-center gap-3.5 px-4 py-3 font-semibold rounded-2xl text-sm transition-all duration-300 focusable ${isActive('/movies')
              ? 'text-white bg-gradient-to-r from-orange-500/20 to-orange-500/5 border border-orange-500/30 shadow-md shadow-orange-500/10'
              : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'
              }`}
          >
            <Film size={19} className={isActive('/movies') ? 'text-orange-400' : 'text-slate-400'} />
            <span>Movies</span>
          </Link>

          <Link
            to="/tv-shows"
            className={`flex items-center gap-3.5 px-4 py-3 font-semibold rounded-2xl text-sm transition-all duration-300 focusable ${isActive('/tv-shows')
              ? 'text-white bg-gradient-to-r from-orange-500/20 to-orange-500/5 border border-orange-500/30 shadow-md shadow-orange-500/10'
              : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'
              }`}
          >
            <Tv size={19} className={isActive('/tv-shows') ? 'text-orange-400' : 'text-slate-400'} />
            <span>TV Shows</span>
          </Link>

          <Link
            to="/livetv"
            className={`flex items-center gap-3.5 px-4 py-3 font-semibold rounded-2xl text-sm transition-all duration-300 focusable ${isActive('/livetv')
              ? 'text-white bg-gradient-to-r from-red-500/20 to-red-500/5 border border-red-500/30 shadow-md shadow-red-500/10'
              : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'
              }`}
          >
            <Radio size={19} className={isActive('/livetv') ? 'text-red-500 animate-pulse' : 'text-slate-400'} />
            <div className="flex items-center justify-between flex-1">
              <span>Live TV</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                LIVE
              </span>
            </div>
          </Link>
        </nav>

        {/* Footer Settings & Watchlist Badge */}
        <div className="p-4 border-t border-white/10 space-y-2">
          {watchlist.length > 0 && (
            <div className="px-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-between text-xs text-slate-300">
              <div className="flex items-center gap-2">
                <Bookmark size={15} className="text-orange-400 fill-orange-400" />
                <span className="font-semibold">My Watchlist</span>
              </div>
              <span className="font-bold px-2 py-0.5 rounded-full bg-orange-500 text-white text-[11px]">
                {watchlist.length}
              </span>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Top Navbar */}
      <header className="sticky top-0 z-40 bg-[#0D0F17]/90 backdrop-blur-2xl border-b border-white/10 lg:hidden px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <span className="text-xl font-extrabold text-white font-display">
            Binge<span className="text-orange-500">HQ</span>
          </span>
        </Link>

        <button
          onClick={() => setIsCmdOpen(true)}
          className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-300 hover:text-white active:scale-95 transition-transform"
          aria-label="Search"
        >
          <Search size={18} className="text-orange-400" />
        </button>
      </header>

      {/* Mobile Bottom Floating Navigation Bar */}
      <nav className="lg:hidden fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-max backdrop-blur-md border border-zinc-800 rounded-3xl p-1 shadow-2xl flex items-center">
        <Link
          to="/"
          className={`flex flex-col items-center px-4 py-2 rounded-2xl transition-all duration-300 ${isActive('/')
            ? 'text-orange-400 bg-orange-500/15 font-bold scale-105'
            : 'text-slate-400 hover:text-slate-200'
            }`}
        >
          <Home size={20} />
          <span className="text-[10px] mt-1">Home</span>
        </Link>

        <Link
          to="/movies"
          className={`flex flex-col items-center px-4 py-2 rounded-2xl transition-all duration-300 ${isActive('/movies')
            ? 'text-orange-400 bg-orange-500/15 font-bold scale-105'
            : 'text-slate-400 hover:text-slate-200'
            }`}
        >
          <Film size={20} />
          <span className="text-[10px] mt-1">Movies</span>
        </Link>

        <Link
          to="/tv-shows"
          className={`flex flex-col items-center px-4 py-2 rounded-2xl transition-all duration-300 ${isActive('/tv-shows')
            ? 'text-orange-400 bg-orange-500/15 font-bold scale-105'
            : 'text-slate-400 hover:text-slate-200'
            }`}
        >
          <Tv size={20} />
          <span className="text-[10px] mt-1">TV Shows</span>
        </Link>

        <Link
          to="/livetv"
          className={`flex flex-col items-center px-4 py-2 rounded-2xl transition-all duration-300 ${isActive('/livetv')
            ? 'text-red-400 bg-red-500/15 font-bold scale-105'
            : 'text-slate-400 hover:text-slate-200'
            }`}
        >
          <Radio size={20} />
          <span className="text-[10px] mt-1">Live TV</span>
        </Link>
      </nav>
    </>
  );
};

export default Navbar;
